import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import {
  createMuiTheme,
  ThemeProvider,
  withStyles,
} from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Hidden from "@material-ui/core/Hidden";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Navigator from "./Navigator";
import Header from "./Header";
import User from "../SignupComponent/User";
import { Route, BrowserRouter as Router } from "react-router-dom";
import QRCode from "../SignupComponent/QRCode";
import MyDocs from "./MyDocs";
import HospitalData from "./HospitalData";
import { ContextGlobal } from "../../context";
import Doctor from "../SignupComponent/Doctor";
import QRReader from "../DashboardComponent/QRReader";
import { useStateWithCallbackLazy } from "use-state-with-callback";
import bot from "../../images/bot.png";

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href="/">
        MedVault
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

let theme = createMuiTheme({
  palette: {
    primary: {
      light: "#63ccff",
      main: "#009be5",
      dark: "#006db3",
    },
  },
  typography: {
    h5: {
      fontWeight: 500,
      fontSize: 26,
      letterSpacing: 0.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  props: {
    MuiTab: {
      disableRipple: true,
    },
  },
  mixins: {
    toolbar: {
      minHeight: 48,
    },
  },
});

theme = {
  ...theme,
  overrides: {
    MuiDrawer: {
      paper: {
        backgroundColor: "#18202c",
      },
    },
    MuiButton: {
      label: {
        textTransform: "none",
      },
      contained: {
        boxShadow: "none",
        "&:active": {
          boxShadow: "none",
        },
      },
    },
    MuiTabs: {
      root: {
        marginLeft: theme.spacing(1),
      },
      indicator: {
        height: 3,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
        backgroundColor: theme.palette.common.white,
      },
    },
    MuiTab: {
      root: {
        textTransform: "none",
        margin: "0 16px",
        minWidth: 0,
        padding: 0,
        [theme.breakpoints.up("md")]: {
          padding: 0,
          minWidth: 0,
        },
      },
    },
    MuiIconButton: {
      root: {
        padding: theme.spacing(1),
      },
    },
    MuiTooltip: {
      tooltip: {
        borderRadius: 4,
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: "#404854",
      },
    },
    MuiListItemText: {
      primary: {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    MuiListItemIcon: {
      root: {
        color: "inherit",
        marginRight: 0,
        "& svg": {
          fontSize: 20,
        },
      },
    },
    MuiAvatar: {
      root: {
        width: 32,
        height: 32,
      },
    },
  },
};

const drawerWidth = 256;

const styles = {
  root: {
    display: "flex",
    minHeight: "100vh",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  app: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  main: {
    flex: 1,
    padding: theme.spacing(6, 4),
    background: "#eaeff1",
  },
  footer: {
    padding: theme.spacing(2),
    background: "#eaeff1",
  },
};

function Paperbase(props) {
  const { classes } = props;
  const { data } = useContext(ContextGlobal);
  const [qrscan, setQrscan] = useStateWithCallbackLazy("No result");
  const [hosData, setHosData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <div className={classes.root}>
          <CssBaseline />
          <nav className={classes.drawer}>
            <Hidden smUp implementation="js">
              <Navigator
                PaperProps={{ style: { width: drawerWidth } }}
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
              />
            </Hidden>
            <Hidden xsDown implementation="css">
              <Navigator PaperProps={{ style: { width: drawerWidth } }} />
            </Hidden>
          </nav>
          <div className={classes.app}>
            {/* profile */}
            <Route path="/dashboard/profile">
              <Header
                onDrawerToggle={handleDrawerToggle}
                logout={props.logout}
                HeaderData={{
                  heading: "Profile",
                  tabs: [
                    { id: "Settings", path: "/dashboard/profile/settings" },
                    { id: "QR-Code", path: "/dashboard/profile/QRcode" },
                  ],
                }}
              />
              <Route path="/dashboard/profile/settings">
                <main className={classes.main}>
                  {data.data.type === "user" ? (
                    <User updateProfile />
                  ) : data.data.type === "doctor" ? (
                    <Doctor updateProfile />
                  ) : (
                    ""
                  )}
                </main>
              </Route>
              <Route path="/dashboard/profile/QRcode">
                <main className={classes.main}>
                  <QRCode />
                </main>
              </Route>
            </Route>

            {/* Hospital data */}
            {data.data.type === "user" ? (
              <Route path="/dashboard/hospitalData">
                <Header
                  onDrawerToggle={handleDrawerToggle}
                  logout={props.logout}
                  HeaderData={{
                    heading: "Hospital Data",
                    tabs: [],
                  }}
                />

                <main className={classes.main}>
                  <HospitalData />
                </main>
              </Route>
            ) : data.data.type === "doctor" ? (
              <Route path="/dashboard/Scanner">
                <Header
                  onDrawerToggle={handleDrawerToggle}
                  logout={props.logout}
                  HeaderData={{
                    heading: "Scanner",
                    tabs: [
                      { id: "Reports", path: "/dashboard/Scanner/reports" },
                      {
                        id: "Prescriptions",
                        path: "/dashboard/Scanner/prescriptions",
                      },
                      { id: "X-rays", path: "/dashboard/Scanner/x-rays" },
                    ],
                  }}
                />
                <Route path="/dashboard/Scanner/reports">
                  <main className={classes.main}>
                    <QRReader
                      qrscan={qrscan}
                      setQrscan={setQrscan}
                      hosData={hosData}
                      setHosData={setHosData}
                      setPatientData={setPatientData}
                    />
                  </main>
                </Route>
                <Route path="/dashboard/Scanner/prescriptions">
                  <main className={classes.main}>
                    <MyDocs qrscan={qrscan} patientData={patientData} />
                  </main>
                </Route>
                <Route path="/dashboard/Scanner/x-rays">
                  <main className={classes.main}>
                    <MyDocs qrscan={qrscan} patientData={patientData} xray />
                  </main>
                </Route>
              </Route>
            ) : (
              ""
            )}

            {/* My Docs */}
            <Route path="/dashboard/myDocs">
              <Header
                onDrawerToggle={handleDrawerToggle}
                logout={props.logout}
                HeaderData={{
                  heading: "My Docs",
                  tabs: [
                    {
                      id: "Prescriptions",
                      path: "/dashboard/myDocs/prescriptions",
                    },
                    { id: "X-rays", path: "/dashboard/myDocs/x-rays" },
                  ],
                }}
              />
              <Route path="/dashboard/myDocs/prescriptions">
                <main className={classes.main}>
                  <MyDocs />
                </main>
              </Route>
              <Route path="/dashboard/myDocs/x-rays">
                <main className={classes.main}>
                  <MyDocs xray />
                </main>
              </Route>
            </Route>

            <footer className={classes.footer}>
              <Copyright />
            </footer>
          </div>
        </div>
        <df-messenger
          intent="WELCOME"
          chat-title="Corona Bot"
          chat-icon={bot}
          agent-id="f8912929-2a2c-47ed-8079-7a46777937a6"
          language-code="en"
          // style="z-index: 1200 !important;position: relative;"
          style={{ position: "relative", zIndex: "1200" }}
        ></df-messenger>
      </ThemeProvider>
    </Router>
  );
}

Paperbase.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Paperbase);
